import type { IPagingFilter } from "@frui.ts/data";
import { bound } from "@frui.ts/helpers";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import TransferRefundPaymentTemplate from "entities/transferRefundPaymentTemplate";
import type TransferRefundResponse from "entities/transferRefundResponse";
import { computed, reaction } from "mobx";
import type RefundRepository from "repositories/refundRepository";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type TransferRefundDetailViewModel from "./transferRefundDetailViewModel";
import type RefundsApprovalService from "services/refundsApprovalService";
import RefundApprovalsListViewModel from "../refundApprovalsListViewModel";
import { transferRefundPaymentTypeChanged } from "services/eventBus";
import RefundType from "entities/refundType";
import RefundApprovalType from "entities/refundApprovalType";
import TransferPaymentType from "entities/transferPaymentType";
import TransferRefundsRequest, { TransferRefundsRequestValidationRules } from "entities/transferRefundsRequest";
import { CounterpartyBankAccountRequestValidationRules } from "entities/counterpartyBankAccountRequest";
import * as selectableListViewModel from "../../../selectableListViewModel";
import { emptyValue } from "utils/emptyValue";

export default abstract class TransferRefundsViewModel extends RefundApprovalsListViewModel<
  TransferRefundResponse,
  ScreenBase,
  TransferRefundsRequest
> {
  readonly modelName = "transferRefund";
  readonly transferRefundType: TransferPaymentType;
  readonly approvalType = RefundApprovalType;

  constructor(
    protected detailViewModelFactory: (
      refundId: string,
      pagingFilter: IPagingFilter,
      refundType: TransferPaymentType
    ) => TransferRefundDetailViewModel,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected repository: RefundRepository,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
  }

  @computed
  get transferRefundPaymentTemplatesOptions() {
    return this.optionsService.entityOptions(TransferRefundPaymentTemplate);
  }

  @computed
  get self(): selectableListViewModel.SelectableListViewModel<TransferRefundsViewModel, TransferRefundResponse> {
    return this as any;
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.senderBankAccount?.accountNumber,
          this.filter.senderBankAccount?.bank,
          this.filter.countryId,
          this.filter.licensePlate,
          this.filter.paidAmount,
          this.filter.postingDate,
          this.filter.refundAmount,
          this.filter.refundPaymentTemplateId,
          this.filter.variableSymbol,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      ),
      // We need to validate bank account separately as it's nested object
      reaction(
        () => [this.filter.senderBankAccount?.accountNumber, this.filter.senderBankAccount?.bank],
        () => {
          this.validateBankAccountFilter();
        }
      ),
      this.eventBus.subscribe(transferRefundPaymentTypeChanged, this.onTransferRefundPaymentTypeChanged)
    );
    super.onInitialize();
  }

  @bound
  validateBankAccountFilter() {
    attachAutomaticValidator(this.filter.senderBankAccount, CounterpartyBankAccountRequestValidationRules);
    validate(this.filter.senderBankAccount);
  }

  @bound
  onItemClick() {
    return (item: TransferRefundResponse) =>
      this.tryActivateChild(this.detailViewModelFactory(item.id, this.pagingFilter, this.transferRefundType));
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    const filter = { ...this.appliedFilter };
    filter.transferType = this.transferRefundType;

    // TODO: Remove the refundType, as it only limits the scope of refunds.
    // Perhaps a new endpoint could be useful?
    this.repository
      .getTransferRefunds(
        { limit: 1, offset: 0 },
        { transferType: this.transferRefundType, approvedBy: RefundApprovalType.ChiefAccountant }
      )
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.getTransferRefunds({ limit, offset }, filter);
  }

  protected createFilter() {
    return attachAutomaticValidator(new TransferRefundsRequest(), TransferRefundsRequestValidationRules);
  }

  @bound
  private onTransferRefundPaymentTypeChanged(data: ReturnType<typeof transferRefundPaymentTypeChanged>) {
    const { originalTransferType } = data.payload;

    if (
      (originalTransferType === TransferPaymentType.Domestic && this.refundType === RefundType.DomesticTransfer) ||
      (originalTransferType === TransferPaymentType.Sepa && this.refundType === RefundType.SepaTransfer)
    ) {
      this.loadData();
    }
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService.entityOptions(TransferRefundPaymentTemplate).find(option => option.id === refundPaymentTemplateId)
        ?.label ?? emptyValue
    );
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName && this.allowWrite) {
      return this.detailViewModelFactory(navigationName, this.pagingFilter, this.transferRefundType);
    }

    return Promise.resolve(undefined);
  }
}
